import { DocumentParameters, HpArticle } from 'api3';
import { Component, Input, ViewEncapsulation, effect, inject, signal } from '@angular/core';
import { Api3Service } from '@app/services/api3.service';
import { LoadingDotsComponent } from '@app/chat/components/loading-dots/loading-dots.component';
import { HpRendererComponent } from '../renderer/hp/hp-renderer.component';
import * as Sentry from '@sentry/angular';
import { MarkdownModule } from 'ngx-markdown';

@Component({
  selector: 'app-hp-articles',
  standalone: true,
  imports: [MarkdownModule, LoadingDotsComponent, HpRendererComponent],
  template: `
    @if (isLoading()) {
      <app-loading-dots />
    } @else {
      <app-hp-renderer [data]="detailsData()" />
    }
  `,
  encapsulation: ViewEncapsulation.None,
})
export class HpArticlesComponent {
  @Input({ required: true }) documentParameters?: DocumentParameters;

  api3Service = inject(Api3Service);

  detailsData = signal<HpArticle[]>([]);
  isLoading = signal(true);

  init = effect(async () => {
    if (!this.documentParameters) {
      console.warn('[HpArticlesComponent] DocumentParameters is undefined or missing.');
      this.isLoading.set(false);
      return;
    }

    const documents = await this._loadArticles(this.documentParameters);
    this.detailsData.set(documents);
    this.isLoading.set(false);
  });

  private async _loadArticles(documentParameters: DocumentParameters): Promise<HpArticle[]> {
    const { data: documents, error } = await this.api3Service.api.rmi.getDocuments({
      type: documentParameters.type,
      carTypeId: documentParameters.carTypeId,
      carTypeGroup: documentParameters.carTypeGroup,
      location: documentParameters.location,
      storyId: documentParameters.storyId,
      tsbType: documentParameters.tsbType,
      systemId: documentParameters.systemId,
      errorCode: documentParameters.errorCode,
    });

    if (error) {
      console.error('[HpArticlesComponent] Error loading articles:', error);

      const sentryError = new Error(`Failed to load article data: ${error.message || 'Unknown error'}`);
      Sentry.captureException(sentryError, {
        tags: {
          location: 'HpArticlesComponent',
          feature: 'ArticleView',
        },
        extra: {
          httpError: error,
        },
      });

      return [];
    }

    return documents || [];
  }
}
